import React, {useState} from "react";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {CircularProgress, Dialog} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {Box, Colors, IconButton, IconClose, Link, Tab, Tabs, Text, Tooltip} from "@bounce/shared/ui";
import {Button} from "../../ui/components";
import {IconDesktop, IconSmartphone} from "../../assets";
import {ReachAccountSearchResult} from "../../apis/requests/accounts/searchAccounts";
import {ReachTemplateLanguage} from "../../apis/requests/templates/types";
import {usePreviewTriggerTemplate} from "../../apis/requests/triggers/previewTriggerTemplate";
import {TriggerInteractionType, TriggerTemplateSummary} from "../../apis/requests/triggers/types";
import LandingPageView from "../templates/template-preview/LandingPageView";
import PhoneFrame from "../templates/template-preview/PhoneFrame";
import PreviewContent, {PreviewMode} from "../templates/template-preview/PreviewContent";
import RenderAsUserSection from "../templates/template-preview/RenderAsUserSection";
import {openInNewTab} from "../../components/ResourcePanel";
import {PreviewTab, accountSummary, previewTabs, templateUrl} from "./helpers";
import {CodeChip} from "./TriggerBadges";
import {LANGUAGE_OPTIONS} from "./triggerDisplay";
import {linkButtonSx, mutedButtonSx} from "./triggerUi";
import SegmentedToggle from "./SegmentedToggle";
import {useAccountPicker, useRememberedAccount} from "./useAccountPicker";
import {useExamplePayload} from "./useExamplePayload";

type Props = {
  interactionType: TriggerInteractionType;
  emailTemplate: TriggerTemplateSummary | null;
  textTemplate: TriggerTemplateSummary | null;
  account: ReachAccountSearchResult | null;
  payload?: Record<string, unknown>;
  initialTab?: PreviewTab;
  onSendTest?: () => void;
};

const PREVIEW_MODE_OPTIONS: {value: PreviewMode; label: React.ReactNode; ariaLabel: string}[] = [
  {value: "mobile", label: <IconSmartphone width={16} height={16} />, ariaLabel: "Mobile"},
  {value: "desktop", label: <IconDesktop width={16} height={16} />, ariaLabel: "Desktop"},
];

const previewTabSx = {
  minHeight: 34,
  minWidth: 0,
  px: 1.75,
  py: 0,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: "normal",
  textTransform: "none",
  color: Colors.grey._600,
  "&.Mui-selected": {color: Colors.blue._800, fontWeight: 600},
} as const;

const TriggerPreviewDialog = NiceModal.create(
  ({interactionType, emailTemplate, textTemplate, account: initialAccount, payload, initialTab, onSendTest}: Props) => {
    const modal = useModal();
    const tabs = previewTabs(emailTemplate, textTemplate);
    const [tab, setTab] = useState<PreviewTab>(
      tabs.some((t) => t.tab === initialTab) ? (initialTab as PreviewTab) : tabs[0]?.tab ?? "email"
    );
    const [language, setLanguage] = useState<ReachTemplateLanguage>("en");
    const [previewMode, setPreviewMode] = useState<PreviewMode>("mobile");
    const [account, setAccount] = useRememberedAccount(initialAccount);
    const picker = useAccountPicker(account, setAccount);
    const current = tabs.find((t) => t.tab === tab) ?? tabs[0];

    const {example, accountFields, ready} = useExamplePayload(interactionType, account);
    const previewPayload = payload && initialAccount ? payload : {...(payload ?? example), ...accountFields};
    const canRender = !!current && !!account && ready;

    const {
      data: preview,
      isFetching,
      error,
    } = usePreviewTriggerTemplate(
      {
        template_id: current?.template.id ?? 0,
        account_id: account?.account_id ?? 0,
        payload: previewPayload,
        language,
      },
      {enabled: canRender, keepPreviousData: true}
    );
    const showsLanguageToggle = (preview?.languages ?? []).includes("es");
    const showsModeToggle = tab === "email";

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.hide()}
        slotProps={{
          transition: {onExited: () => modal.remove()},
          paper: {sx: {borderRadius: "10px", maxWidth: 900, width: "100%", backgroundColor: Colors.bw.white}},
        }}
        maxWidth={false}
      >
        <Box sx={{display: "flex", alignItems: "center", gap: 1.5, p: "20px 26px 14px"}}>
          <Text size={20} variant="inter600">
            Preview
          </Text>
          {current && (
            <Tooltip title="Open template in a new tab">
              <Link
                component="button"
                type="button"
                underline="none"
                onClick={() => openInNewTab(templateUrl(current.template.id))}
                sx={{display: "inline-flex", alignItems: "center", gap: 0.5, color: Colors.blue._800}}
              >
                <CodeChip>{preview?.template_key ?? current.template.template_key}</CodeChip>
                <OpenInNewIcon sx={{fontSize: 15}} />
              </Link>
            </Tooltip>
          )}
          <Box sx={{flex: 1}} />
          <IconButton onClick={() => modal.hide()} size="small" aria-label="Close preview">
            <IconClose width={20} height={20} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: "26px",
            borderBottom: `1px solid ${Colors.tertiary._300}`,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, next: PreviewTab) => setTab(next)}
            sx={{
              minHeight: 34,
              "& .MuiTabs-flexContainer": {gap: 1.25},
              "& .MuiTabs-indicator": {backgroundColor: Colors.blue._800},
            }}
          >
            {tabs.map((t) => (
              <Tab key={t.tab} value={t.tab} label={t.label} disableRipple sx={previewTabSx} />
            ))}
          </Tabs>
          <Box sx={{flex: 1}} />
          <Box sx={{display: "flex", alignItems: "center", gap: 1.25, pb: 1}}>
            {showsLanguageToggle && (
              <SegmentedToggle value={language} options={LANGUAGE_OPTIONS} onChange={setLanguage} />
            )}
            {showsModeToggle && (
              <SegmentedToggle value={previewMode} options={PREVIEW_MODE_OPTIONS} onChange={setPreviewMode} />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            minHeight: 520,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            p: "26px",
            backgroundColor: Colors.tertiary._100,
            position: "relative",
          }}
        >
          {!account ? (
            <Box sx={{width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", gap: 1.5}}>
              <Text size={14} variant="inter500">
                Pick an account to render the preview for
              </Text>
              <RenderAsUserSection {...picker} />
            </Box>
          ) : error ? (
            <Text size={13} sx={{color: Colors.red._800}}>
              The preview could not be rendered.
            </Text>
          ) : !preview ? (
            <CircularProgress size={24} sx={{mt: 10}} />
          ) : tab === "landing" ? (
            <PhoneFrame>
              <LandingPageView html={preview.rendered_landing_page ?? ""} />
            </PhoneFrame>
          ) : previewMode === "desktop" && tab === "email" ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: 800,
                height: 560,
                overflow: "auto",
                borderRadius: "8px",
                border: `1px solid ${Colors.tertiary._300}`,
                backgroundColor: Colors.bw.white,
              }}
            >
              <PreviewContent contentChannel={preview.content_channel} renderResult={preview} previewMode="desktop" />
            </Box>
          ) : (
            <PhoneFrame>
              <PreviewContent contentChannel={preview.content_channel} renderResult={preview} previewMode="mobile" />
            </PhoneFrame>
          )}
          {isFetching && preview && (
            <CircularProgress size={16} sx={{position: "absolute", top: 12, right: 16, color: Colors.grey._600}} />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: "14px 26px 18px",
            borderTop: `1px solid ${Colors.tertiary._300}`,
          }}
        >
          <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25}}>
            {account && (
              <>
                <Text size={12} sx={{color: Colors.grey._600}}>
                  Rendered for
                </Text>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                  <Text
                    size={12}
                    sx={{fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                  >
                    {accountSummary(account)}
                  </Text>
                  {!initialAccount && (
                    <Box component="button" type="button" onClick={() => setAccount(null)} sx={linkButtonSx}>
                      Change
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
          <Button buttonType="outlined-primary" size="small" onClick={() => modal.hide()} sx={mutedButtonSx}>
            Close
          </Button>
          {onSendTest && (
            <Button
              buttonType="contained-primary"
              size="small"
              onClick={() => {
                modal.hide();
                onSendTest();
              }}
            >
              Send test
            </Button>
          )}
        </Box>
      </Dialog>
    );
  }
);

export default TriggerPreviewDialog;
